import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Lightweight native lightbox for the public gallery. No external deps.
// Ends do NOT wrap — prev/next disable at the boundaries (consistent,
// predictable). Body scroll is locked while open and restored on close.
const Lightbox = ({ list, index, setIndex, onClose, t }) => {
  const closeRef = useRef(null);
  const touchStartX = useRef(null);
  const total = list.length;
  const im = list[index];

  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), [setIndex]);
  const next = useCallback(
    () => setIndex((i) => Math.min(total - 1, i + 1)),
    [setIndex, total]
  );

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, prev, next]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const dx = endX - touchStartX.current;
    touchStartX.current = null;
    if (dx > 50) prev();
    else if (dx < -50) next();
  };

  const navBtn =
    "absolute top-1/2 -translate-y-1/2 p-2.5 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors disabled:opacity-25 disabled:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("public.gallery.title")}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="public-gallery-lightbox"
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label={t("public.lightbox.close")}
        className="absolute top-3 right-3 sm:top-5 sm:right-5 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/60 z-10"
        data-testid="public-lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); prev(); }}
        disabled={index === 0}
        aria-label={t("public.lightbox.prev")}
        className={`${navBtn} left-2 sm:left-5`}
        data-testid="public-lightbox-prev"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <img
        src={`${BACKEND_URL}${im.url}`}
        alt={t("public.gallery.photoAlt", index + 1, total)}
        className="max-h-[85vh] max-w-[92vw] sm:max-w-[85vw] object-contain rounded-lg select-none"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
        data-testid="public-lightbox-image"
      />
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); next(); }}
        disabled={index === total - 1}
        aria-label={t("public.lightbox.next")}
        className={`${navBtn} right-2 sm:right-5`}
        data-testid="public-lightbox-next"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
      <span
        className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white/90 text-sm tabular-nums"
        data-testid="public-lightbox-counter"
      >
        {t("public.lightbox.counter", index + 1, total)}
      </span>
    </div>
  );
};

// "Our Work" photo gallery — renders ONLY when at least one gallery image
// exists. Equal-weight 2-column grid (all counts >= 2) so sequential
// before/after uploads naturally land side-by-side. Photos remain plain
// ordered images — no pairing metadata. Clicking any photo opens the
// lightbox. All grid images lazy-load inside fixed-aspect containers.
export const PublicBusinessGallery = ({ images, t }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const list = (images || []).filter((im) => im && im.url && im.id);
  if (list.length === 0) return null;
  const total = list.length;

  const Cell = ({ im, i, className = "" }) => (
    <button
      type="button"
      onClick={() => setOpenIndex(i)}
      aria-label={t("public.gallery.photoAlt", i + 1, total)}
      className={`block w-full text-left rounded-xl overflow-hidden bg-gray-100 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2 ${className}`}
      data-testid={`public-gallery-open-${im.id}`}
    >
      <img
        src={`${BACKEND_URL}${im.url}`}
        alt={t("public.gallery.photoAlt", i + 1, total)}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.02]"
        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
        data-testid={`public-gallery-image-${im.id}`}
      />
    </button>
  );

  return (
    <section
      className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-10 lg:p-14 mb-5 lg:mb-8"
      data-testid="public-gallery-section"
    >
      <span className="block w-10 h-1 rounded-full bg-green-700 mb-3" aria-hidden="true" />
      <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 tracking-tight">
        {t("public.gallery.title")}
      </h2>
      <div className="mt-4 lg:mt-8">
        {total === 1 ? (
          <Cell im={list[0]} i={0} className="aspect-video lg:max-w-4xl" />
        ) : (
          <div className="grid grid-cols-2 gap-2.5 lg:gap-4">
            {list.map((im, i) => (
              <Cell key={im.id} im={im} i={i} className="aspect-[4/3]" />
            ))}
          </div>
        )}
      </div>
      {openIndex !== null && (
        <Lightbox
          list={list}
          index={openIndex}
          setIndex={setOpenIndex}
          onClose={() => setOpenIndex(null)}
          t={t}
        />
      )}
    </section>
  );
};
